<template>
  <div data-test="experience-stories">
    <div class="flex items-center justify-between gap-2">
      <div>
        <h3 class="text-sm font-semibold text-foreground">Your stories</h3>
        <p class="text-xs text-muted-foreground">
          Details you gave while writing letters. Every cover letter picks the ones that fit the job.
        </p>
      </div>
      <button
        v-if="!adding"
        type="button"
        class="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        data-test="experience-stories-add"
        @click="adding = true"
      >
        <i class="fas fa-plus text-[10px]"></i> Add
      </button>
    </div>

    <div v-if="adding" class="mt-3 rounded-lg border border-border bg-card p-3">
      <textarea
        v-model="newAnswer"
        rows="3"
        maxlength="1000"
        placeholder="e.g. My postage calculator replaced a manual spreadsheet and saved the team about 10 hours a week."
        class="block w-full rounded-lg border border-border bg-background p-2 text-xs text-foreground resize-none focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
      ></textarea>
      <div class="mt-2 flex justify-end gap-2">
        <button type="button" class="px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground" @click="cancelAdd">Cancel</button>
        <button
          type="button"
          :disabled="saving || !newAnswer.trim()"
          class="px-3 py-1 rounded-lg bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-40"
          @click="addStory"
        >
          Save
        </button>
      </div>
    </div>

    <p v-if="loading" class="mt-3 text-xs text-muted-foreground">
      <i class="fas fa-spinner fa-spin mr-1"></i> Loading…
    </p>
    <p v-else-if="!stories.length && !adding" class="mt-3 text-xs text-muted-foreground">
      No stories yet. When you write a cover letter, we'll ask about results your resume doesn't mention.
    </p>

    <ul v-else class="mt-3 space-y-2">
      <li
        v-for="story in stories"
        :key="story.id"
        class="rounded-lg border border-border bg-card p-3"
        data-test="experience-story"
      >
        <p v-if="story.question" class="text-[11px] text-muted-foreground">{{ story.question }}</p>
        <textarea
          v-if="editingId === story.id"
          v-model="editAnswer"
          rows="3"
          maxlength="1000"
          class="mt-1 block w-full rounded-lg border border-border bg-background p-2 text-xs text-foreground resize-none focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
        ></textarea>
        <p v-else class="mt-1 text-sm text-foreground whitespace-pre-wrap break-words">{{ story.answer }}</p>
        <div class="mt-2 flex flex-wrap items-center justify-between gap-2">
          <span v-if="story.experience_label" class="text-[11px] text-muted-foreground">
            <i class="fas fa-briefcase mr-1 text-[10px]"></i>{{ story.experience_label }}
          </span>
          <span v-else></span>
          <div class="flex items-center gap-2">
            <template v-if="editingId === story.id">
              <button type="button" class="text-[11px] text-muted-foreground hover:text-foreground" @click="editingId = null">Cancel</button>
              <button
                type="button"
                :disabled="saving || !editAnswer.trim()"
                class="text-[11px] font-semibold text-primary hover:underline disabled:opacity-40"
                @click="saveEdit(story)"
              >
                Save
              </button>
            </template>
            <template v-else>
              <button type="button" class="text-[11px] text-muted-foreground hover:text-primary" @click="startEdit(story)">
                <i class="fas fa-pen mr-0.5 text-[10px]"></i> Edit
              </button>
              <button type="button" class="text-[11px] text-muted-foreground hover:text-red-500" @click="removeStory(story)">
                <i class="fas fa-trash mr-0.5 text-[10px]"></i> Delete
              </button>
            </template>
          </div>
        </div>
      </li>
    </ul>
    <p v-if="error" class="mt-2 text-xs text-red-500">{{ error }}</p>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import experienceStoriesService from "@/services/experienceStories";

export default {
  name: "ExperienceStoriesList",
  setup() {
    const stories = ref([]);
    const loading = ref(true);
    const saving = ref(false);
    const error = ref("");
    const editingId = ref(null);
    const editAnswer = ref("");
    const adding = ref(false);
    const newAnswer = ref("");

    const run = async (fn, message) => {
      saving.value = true;
      error.value = "";
      try {
        await fn();
      } catch (e) {
        error.value = message;
      } finally {
        saving.value = false;
      }
    };

    onMounted(async () => {
      try {
        stories.value = await experienceStoriesService.list();
      } catch (e) {
        error.value = "Couldn't load your stories.";
      } finally {
        loading.value = false;
      }
    });

    const startEdit = (story) => {
      editingId.value = story.id;
      editAnswer.value = story.answer;
    };

    const saveEdit = (story) => run(async () => {
      const updated = await experienceStoriesService.update(story.id, editAnswer.value.trim());
      stories.value = stories.value.map((s) => (s.id === story.id ? updated : s));
      editingId.value = null;
    }, "Couldn't save that change.");

    // No confirm dialog: a story is one sentence and easy to add again.
    const removeStory = (story) => run(async () => {
      await experienceStoriesService.remove(story.id);
      stories.value = stories.value.filter((s) => s.id !== story.id);
    }, "Couldn't delete that story.");

    const cancelAdd = () => {
      adding.value = false;
      newAnswer.value = "";
    };

    const addStory = () => run(async () => {
      const created = await experienceStoriesService.save({ answer: newAnswer.value.trim(), source: "manual" });
      stories.value = [created, ...stories.value];
      cancelAdd();
    }, "Couldn't save that story.");

    return {
      stories, loading, saving, error, editingId, editAnswer, adding, newAnswer,
      startEdit, saveEdit, removeStory, cancelAdd, addStory,
    };
  },
};
</script>
